import { graphqlQuery } from '@/lib/api/graphql';
import type { GraphQLCursorConnection } from '@/lib/graphql-pagination';
import type { CaseAuditLog } from '@/types/collections';
import { useCursorPagination } from './useCursorPagination';

// --- GraphQL types ---

interface DisputeAuditLogGraphQLNode {
  auditLogId: number;
  disputeId: number;
  actionType: string | null;
  description: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  metadata: string | null;
  transactionId: number | null;
  transactionNumber: string | null;
  transactionAmount: number | null;
  transactionDate: string | null;
  createdByUserName: string | null;
  createdAt: string;
}

interface DisputeAuditLogListGraphQLResponse {
  disputes: {
    disputeAuditLogList: GraphQLCursorConnection<DisputeAuditLogGraphQLNode>;
  };
}

// --- GraphQL query ---

const DISPUTE_AUDIT_LOG_LIST_QUERY = `
  query DisputeAuditLogList($first: Int, $after: String, $sort: [DisputeAuditLogListSortSortByInput!], $filter: DisputeAuditLogListFilterInput) {
    disputes {
      disputeAuditLogList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            auditLogId
            disputeId
            actionType
            description
            fieldName
            oldValue
            newValue
            metadata
            transactionId
            transactionNumber
            transactionAmount
            transactionDate
            createdByUserName
            createdAt
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

const AUDIT_LOG_PAGE_SIZE = 25;

// --- Mapping ---

const actionTypeToActionMap: Record<string, CaseAuditLog['action']> = {
  disputecreated: 'case_created',
  statuschanged: 'status_change',
  prioritychanged: 'priority_change',
  assignmentchanged: 'assignment_change',
  stageadvanced: 'field_update',
  disputeresolved: 'status_change',
  disputeclosed: 'status_change',
  disputereopened: 'status_change',
  noteadded: 'note_added',
  fieldupdated: 'field_update',
  phonecalllogged: 'field_update',
  emailsent: 'field_update',
  transactionadded: 'transaction_added',
  transactionremoved: 'transaction_removed',
};

function mapGraphQLNodeToDisputeAuditLog(node: DisputeAuditLogGraphQLNode): CaseAuditLog {
  const actionName = (node.actionType ?? '').toLowerCase().replace(/\s+/g, '');
  return {
    id: String(node.auditLogId),
    caseId: String(node.disputeId),
    action: actionTypeToActionMap[actionName] || 'field_update',
    description: node.description,
    fieldName: node.fieldName || undefined,
    oldValue: node.oldValue,
    newValue: node.newValue,
    transactionId: node.transactionId != null ? String(node.transactionId) : undefined,
    transactionNumber: node.transactionNumber || undefined,
    transactionAmount: node.transactionAmount ?? undefined,
    transactionDate: node.transactionDate ? new Date(node.transactionDate) : undefined,
    createdBy: node.createdByUserName || 'System',
    createdAt: new Date(node.createdAt),
  };
}

// --- Mock data builder ---

function buildMockDisputeAuditLogs(disputeId: string): CaseAuditLog[] {
  return [
    {
      id: 'audit-5',
      caseId: disputeId,
      action: 'priority_change',
      description: 'Priority updated',
      fieldName: 'priority',
      oldValue: 'Medium',
      newValue: 'High',
      createdBy: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-4',
      caseId: disputeId,
      action: 'note_added',
      description: 'Note added to dispute',
      createdBy: 'John Smith',
      createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-3',
      caseId: disputeId,
      action: 'status_change',
      description: 'Dispute status changed',
      fieldName: 'status',
      oldValue: 'New',
      newValue: 'In Progress',
      createdBy: 'John Smith',
      createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-2',
      caseId: disputeId,
      action: 'assignment_change',
      description: 'Dispute assigned to team member',
      fieldName: 'assignedUser',
      oldValue: 'Unassigned',
      newValue: 'John Smith',
      createdBy: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'audit-1',
      caseId: disputeId,
      action: 'case_created',
      description: 'Dispute case created',
      createdBy: 'System',
      createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    },
  ];
}

// --- Hook ---

export interface DisputeAuditLogsGraphQLResult {
  auditLogs: CaseAuditLog[];
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  reset: () => void;
  refetch: () => void;
}

export function useDisputeAuditLogsGraphQL(disputeId: string | undefined): DisputeAuditLogsGraphQLResult {
  const result = useCursorPagination<DisputeAuditLogGraphQLNode, CaseAuditLog>(
    {
      pageSize: AUDIT_LOG_PAGE_SIZE,
      buildMockData: () => (disputeId ? buildMockDisputeAuditLogs(disputeId) : []),
      executeQuery: async (apiConfig, afterCursor, signal) => {
        if (!disputeId) {
          return { edges: [], pageInfo: { endCursor: null, hasNextPage: false } };
        }
        const variables: Record<string, unknown> = {
          first: AUDIT_LOG_PAGE_SIZE,
          sort: [{ field: 'CreatedAt', order: 'Descending' }],
          filter: { disputeId: { eq: Number(disputeId) } },
        };
        if (afterCursor) {
          variables.after = afterCursor;
        }
        const response = await graphqlQuery<DisputeAuditLogListGraphQLResponse>(
          apiConfig,
          DISPUTE_AUDIT_LOG_LIST_QUERY,
          variables,
          signal,
        );
        return response.disputes.disputeAuditLogList;
      },
      mapNode: mapGraphQLNodeToDisputeAuditLog,
    },
    [disputeId],
  );

  return {
    auditLogs: result.items,
    isLoading: result.isLoading,
    isFetchingMore: result.isFetchingMore,
    hasMore: result.hasMore,
    loadMore: result.loadMore,
    reset: result.reset,
    refetch: result.refetch,
  };
}
